import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

const AI_CONFIG_KEY = "ai_config";
const MAINTENANCE_KEY = "maintenance_mode";

const DEFAULT_AI_CONFIG = {
  anthropic_model: "claude-haiku-4-5-20251001",
  max_tokens: 1024,
  agent_refresh_seconds: 60,
};

const DEFAULT_MAINTENANCE = {
  enabled: false,
  message: "We're performing scheduled maintenance. Please check back shortly.",
};

function maskKey(v: string | undefined | null): string | null {
  if (!v) return null;
  if (v.length <= 8) return "•".repeat(v.length);
  return `${v.slice(0, 4)}••••${v.slice(-4)}`;
}

// ---------- Secret status (masked) ----------
const TRACKED_KEYS = [
  "TWELVE_DATA_API_KEY",
  "FMP_API_KEY",
  "NEWS_API_KEY",
  "FRED_API_KEY",
  "ANTHROPIC_API_KEY",
  "TELEGRAM_BOT_TOKEN",
  "TELEGRAM_CHAT_ID",
  "STRIPE_SANDBOX_API_KEY",
  "PAYMENTS_SANDBOX_WEBHOOK_SECRET",
  "LOVABLE_API_KEY",
] as const;

export const adminListSecretStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const keys = TRACKED_KEYS.map((name) => {
      const v = process.env[name];
      return {
        name,
        set: Boolean(v),
        masked: maskKey(v),
        length: v?.length ?? 0,
      };
    });
    return { keys };
  });

// ---------- Test connection ----------
async function timed(
  url: string,
  init?: RequestInit,
): Promise<{ ok: boolean; status: number; latencyMs: number; error?: string }> {
  const t0 = Date.now();
  try {
    const r = await fetch(url, init);
    return { ok: r.ok, status: r.status, latencyMs: Date.now() - t0 };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      latencyMs: Date.now() - t0,
      error: (e as Error).message,
    };
  }
}

export const adminTestService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        service: z.enum(["twelve_data", "fmp", "news_api", "fred", "anthropic", "telegram"]),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    switch (data.service) {
      case "twelve_data": {
        const k = process.env.TWELVE_DATA_API_KEY;
        if (!k) return { ok: false, error: "API key not set", latencyMs: 0 };
        return timed(`https://api.twelvedata.com/quote?symbol=EUR/USD&apikey=${k}`);
      }
      case "fmp": {
        const k = process.env.FMP_API_KEY;
        if (!k) return { ok: false, error: "API key not set", latencyMs: 0 };
        return timed(`https://financialmodelingprep.com/stable/quote?symbol=AAPL&apikey=${k}`);
      }
      case "news_api": {
        const k = process.env.NEWS_API_KEY;
        if (!k) return { ok: false, error: "API key not set", latencyMs: 0 };
        return timed(
          `https://newsapi.org/v2/top-headlines?country=us&pageSize=1&apiKey=${k}`,
        );
      }
      case "fred": {
        const k = process.env.FRED_API_KEY;
        if (!k) return { ok: false, error: "API key not set", latencyMs: 0 };
        return timed(
          `https://api.stlouisfed.org/fred/series?series_id=GDP&api_key=${k}&file_type=json`,
        );
      }
      case "anthropic": {
        const k = process.env.ANTHROPIC_API_KEY;
        if (!k) return { ok: false, error: "API key not set", latencyMs: 0 };
        const t0 = Date.now();
        try {
          const r = await fetch("https://api.anthropic.com/v1/messages", {
            method: "POST",
            headers: {
              "x-api-key": k,
              "anthropic-version": "2023-06-01",
              "content-type": "application/json",
            },
            body: JSON.stringify({
              model: "claude-haiku-4-5-20251001",
              max_tokens: 10,
              messages: [{ role: "user", content: "test" }],
            }),
          });
          const latencyMs = Date.now() - t0;
          if (r.ok) return { ok: true, status: r.status, latencyMs };
          const body = await r.text().catch(() => "");
          const label =
            r.status === 401
              ? "Invalid API key (401)"
              : r.status === 429
                ? "Rate limited (429)"
                : r.status === 402
                  ? "No credits (402)"
                  : r.status === 529
                    ? "Anthropic overloaded (529)"
                    : `HTTP ${r.status}`;
          return { ok: false, status: r.status, latencyMs, error: `${label} — ${body.slice(0, 160)}` };
        } catch (e) {
          return { ok: false, status: 0, latencyMs: Date.now() - t0, error: (e as Error).message };
        }
      }
      case "telegram": {
        const k = process.env.TELEGRAM_BOT_TOKEN;
        if (!k) return { ok: false, error: "Bot token not set", latencyMs: 0 };
        return timed(`https://api.telegram.org/bot${k}/getMe`);
      }
    }
  });

// ---------- AI config ----------
const AI_MODELS = [
  "claude-haiku-4-5-20251001",
  "claude-sonnet-4-5",
  "claude-opus-4-5",
  "claude-3-5-haiku-latest",
  "claude-3-5-sonnet-latest",
] as const;

export const adminGetAiConfig = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", AI_CONFIG_KEY)
      .maybeSingle();
    return {
      config: (data?.value as typeof DEFAULT_AI_CONFIG | null) ?? DEFAULT_AI_CONFIG,
      models: AI_MODELS,
    };
  });

export const adminSaveAiConfig = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        anthropic_model: z.enum(AI_MODELS),
        max_tokens: z.number().int().min(64).max(8192),
        agent_refresh_seconds: z.number().int().min(15).max(3600),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("site_settings").upsert({
      key: AI_CONFIG_KEY,
      value: data as never,
      updated_at: new Date().toISOString(),
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Maintenance mode ----------
export const adminGetMaintenance = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", MAINTENANCE_KEY)
      .maybeSingle();
    return {
      maintenance:
        (data?.value as typeof DEFAULT_MAINTENANCE | null) ?? DEFAULT_MAINTENANCE,
    };
  });

export const adminSaveMaintenance = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        enabled: z.boolean(),
        message: z.string().min(1).max(500),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("site_settings").upsert({
      key: MAINTENANCE_KEY,
      value: data as never,
      updated_at: new Date().toISOString(),
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Public read for site gate
export const getMaintenancePublic = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("value")
    .eq("key", MAINTENANCE_KEY)
    .maybeSingle();
  const m = (data?.value as typeof DEFAULT_MAINTENANCE | null) ?? DEFAULT_MAINTENANCE;
  return { enabled: m.enabled, message: m.message };
});
